import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Slider } from "@/components/ui/slider";

const GREETING = {
    en: "hi, i'm pax. i'm here whenever you need me.",
    hi: "नमस्ते, मैं पैक्स हूँ। जब भी आपको ज़रूरत हो, मैं यहीं हूँ।",
};

export default function VoicePicker({ open, onClose, currentVoice, currentLocale = "en", currentSpeed = 1, onVoiceChange }) {
    const [catalog, setCatalog] = useState([]);
    const [lang, setLang] = useState(currentLocale);
    const [selected, setSelected] = useState(currentVoice);
    const [speed, setSpeed] = useState(currentSpeed);
    const [busy, setBusy] = useState(false);
    const [previewing, setPreviewing] = useState(null);
    const previewRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        api.get("/voices").then(r => setCatalog(r.data)).catch(() => {});
        setLang(currentLocale);
        setSelected(currentVoice);
        setSpeed(currentSpeed);
    }, [open, currentVoice, currentLocale, currentSpeed]);

    useEffect(() => () => previewRef.current?.pause(), []);

    const group = catalog.find(g => g.language === lang) || catalog[0];
    const voices = group?.voices || [];
    const effectiveSelected = voices.some(v => v.key === selected) ? selected : voices[0]?.key;

    const preview = async (key, e) => {
        e.stopPropagation();
        previewRef.current?.pause();
        if (previewing === key) { setPreviewing(null); return; }
        setPreviewing(key);
        try {
            const res = await api.post("/tts", { text: GREETING[lang] || GREETING.en, voice: key, speed }, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const audio = new Audio(url);
            previewRef.current = audio;
            audio.onended = () => { setPreviewing(null); URL.revokeObjectURL(url); };
            audio.onerror = () => { setPreviewing(null); URL.revokeObjectURL(url); };
            await audio.play();
        } catch {
            setPreviewing(null);
            toast.error("voice preview unavailable right now");
        }
    };

    const save = async () => {
        if (!effectiveSelected) return;
        setBusy(true);
        try {
            const [{ data }] = await Promise.all([
                api.put("/users/me/voice", { voice: effectiveSelected }),
                api.put("/users/me/preferences", { voice_speed: speed }),
            ]);
            onVoiceChange?.({ ...data, voice_speed: speed });
            trackEvent("voice_changed", { voice: effectiveSelected, locale: data.locale, speed });
            toast.success(data.locale !== currentLocale ? "voice and language updated" : "voice updated");
            onClose();
        } catch {
            toast.error("couldn't save voice");
        } finally {
            setBusy(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="fixed inset-0 z-50 bg-ink/30 backdrop-blur-sm flex items-center justify-center p-6"
                    data-testid="voice-picker-overlay"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                        onClick={e => e.stopPropagation()}
                        className="bg-bone border border-clayHover rounded-3xl p-8 w-full max-w-md shadow-clay max-h-[85vh] overflow-y-auto"
                    >
                        <div className="flex items-start justify-between mb-5">
                            <div>
                                <h2 className="font-heading text-2xl tracking-tight text-ink">choose pax's voice</h2>
                                <p className="text-sm text-inkSoft font-body mt-1">pick a language, then a voice that feels like home.</p>
                            </div>
                            <button onClick={onClose} className="text-inkSoft hover:text-ink" data-testid="voice-picker-close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="inline-flex items-center gap-1 bg-clay/60 border border-clayHover rounded-full p-1 mb-4" data-testid="voice-lang-tabs">
                            {catalog.map(g => (
                                <button
                                    key={g.language}
                                    onClick={() => setLang(g.language)}
                                    data-testid={`voice-lang-${g.language}`}
                                    className={`px-4 py-1.5 rounded-full text-sm font-body transition-colors ${
                                        g.language === lang ? "bg-white text-ink shadow-soft" : "text-inkSoft hover:text-ink"
                                    }`}
                                >
                                    {g.label}
                                </button>
                            ))}
                        </div>

                        <div className="space-y-2 mb-6">
                            {voices.map(v => (
                                <button
                                    key={v.key}
                                    onClick={() => setSelected(v.key)}
                                    data-testid={`voice-option-${v.key}`}
                                    className={`w-full flex items-center justify-between rounded-2xl border px-5 py-4 transition-all font-body ${
                                        effectiveSelected === v.key
                                            ? "bg-terracotta/10 border-terracotta text-ink"
                                            : "bg-white border-clayHover text-ink hover:bg-clay"
                                    }`}
                                >
                                    <span className="text-left">{v.label}</span>
                                    <span className="flex items-center gap-3">
                                        <span
                                            role="button"
                                            aria-label={`preview ${v.key}`}
                                            data-testid={`voice-preview-${v.key}`}
                                            onClick={(e) => preview(v.key, e)}
                                            className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                                previewing === v.key ? "bg-terracotta text-bone" : "bg-clay text-inkSoft hover:text-terracotta"
                                            }`}
                                        >
                                            {previewing === v.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" fill="currentColor" />}
                                        </span>
                                        {effectiveSelected === v.key && <Check className="w-4 h-4 text-terracotta" />}
                                    </span>
                                </button>
                            ))}
                        </div>

                        <div className="bg-white/70 border border-clayHover rounded-2xl px-5 py-4 mb-6" data-testid="voice-speed-card">
                            <div className="flex items-center justify-between mb-3 text-sm font-body">
                                <span className="text-ink">pace</span>
                                <span className="text-inkSoft" data-testid="voice-speed-value">
                                    {speed < 0.95 ? "slower" : speed > 1.05 ? "a little faster" : "gentle"} · {speed.toFixed(2)}×
                                </span>
                            </div>
                            <Slider
                                data-testid="voice-speed-slider"
                                min={0.7} max={1.2} step={0.05}
                                value={[speed]}
                                onValueChange={([v]) => setSpeed(v)}
                            />
                            <div className="flex justify-between text-[11px] uppercase tracking-widest text-inkSoft mt-2 font-body">
                                <span>slow</span><span>faster</span>
                            </div>
                        </div>

                        <button
                            data-testid="voice-picker-save"
                            onClick={save}
                            disabled={busy || !effectiveSelected}
                            className="w-full bg-terracotta text-bone rounded-full py-3 hover:bg-terracottaHover transition-colors font-body disabled:opacity-60"
                        >
                            {busy ? "saving…" : lang !== currentLocale ? "save · pax will reply in this language" : "save"}
                        </button>
                        <p className="text-xs text-inkSoft text-center mt-3 font-body">
                            pax speaks slowly and softly, in a natural AI voice. if voice services are busy, your browser's built-in voice steps in.
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
